import { jwtCookieName } from "services/CookieService";
import ApplicationConfig from "configs/ApplicationConfig";
import { container } from "tsyringe";

export enum ContentType {
	Json = "application/json",
	FormData = "multipart/form-data;",
	Zip = "application/zip",
}

export default abstract class BaseApi {
	protected readonly apiUrl = `${container.resolve(ApplicationConfig).get().api.rootUrl}/api/v1`;

	private static investorRevisions = new Map<string, string>();
	private static investorSubscriptions = new Map<string, string>();

	protected buildHeaders(contentType: ContentType, url?: string, body?: unknown) {
		const headers = new Headers();
		if (contentType === ContentType.Json) headers.set("Content-Type", contentType);
		const match = url?.match(/\/(?:subscriptions(?:\/draft-subscription)?|product_subscribed)\/([0-9a-f-]{36})(?:\/|$)/i);
		const payload = body && typeof body === "object" ? (body as Record<string, unknown>) : {};
		const id = match?.[1] ?? payload["subscriptionId"] ?? payload["productSubscribedId"];
		const revision = typeof id === "string" ? BaseApi.investorRevisions.get(BaseApi.investorSubscriptions.get(id) ?? id) : undefined;
		if (revision !== undefined) headers.set("X-Investor-Revision", revision);
		return headers;
	}

	protected buildBody(body: { [key: string]: unknown }): string {
		return JSON.stringify(body);
	}

	protected async getRequest<T>(url: string, body?: { [key: string]: unknown }) {
		const request = async () => {
			return fetch(url, {
				method: "GET",
				credentials: "include",
				headers: this.buildHeaders(ContentType.Json, url, body),
				body: body ? this.buildBody(body) : undefined,
			});
		};

		return request().then((response) => this.processResponse<T>(response, false));
	}

	protected async postRequest<T>(url: string, body: { [key: string]: unknown } = {}) {
		const request = async () => {
			return fetch(url, {
				method: "POST",
				credentials: "include",
				headers: this.buildHeaders(ContentType.Json, url, body),
				body: this.buildBody(body),
			});
		};

		return request().then((response) => this.processResponse<T>(response));
	}

	protected async postRequestFormdata<T>(url: string, body: FormData) {
		const request = async () => {
			return fetch(url, {
				method: "POST",
				credentials: "include",
				headers: this.buildHeaders(ContentType.FormData, url, body),
				body: body,
			});
		};

		return request().then((response) => this.processResponse<T>(response));
	}

	protected async putRequest<T>(url: string, body: { [key: string]: unknown } = {}) {
		const request = async () => {
			return fetch(url, {
				method: "PUT",
				credentials: "include",
				headers: this.buildHeaders(ContentType.Json, url, body),
				body: this.buildBody(body),
			});
		};

		return request().then((response) => this.processResponse<T>(response));
	}

	protected async putRequestFormdata<T>(url: string, body: FormData) {
		const request = async () => {
			return fetch(url, {
				method: "PUT",
				credentials: "include",
				headers: this.buildHeaders(ContentType.FormData, url, body),
				body: body,
			});
		};

		return request().then((response) => this.processResponse<T>(response));
	}

	protected async patchRequest<T>(url: string, body: { [key: string]: unknown } = {}) {
		const request = async () => {
			return fetch(url, {
				method: "PATCH",
				credentials: "include",
				headers: this.buildHeaders(ContentType.Json, url, body),
				body: this.buildBody(body),
			});
		};

		return request().then((response) => this.processResponse<T>(response));
	}

	protected async deleteRequest<T>(url: string, body: { [key: string]: unknown } = {}) {
		const request = async () => {
			return fetch(url, {
				method: "DELETE",
				credentials: "include",
				headers: this.buildHeaders(ContentType.Json, url, body),
				body: this.buildBody(body),
			});
		};

		return request().then((response) => this.processResponse<T>(response));
	}

	protected async patchFormDataRequest<T>(url: string, body: FormData) {
		const request = async () => {
			return fetch(url, {
				method: "PATCH",
				credentials: "include",
				headers: this.buildHeaders(ContentType.FormData, url, body),
				body,
			});
		};

		return request().then((response) => this.processResponse<T>(response));
	}

	protected async processResponse<T>(response: Response, mutation = true): Promise<T> {
		let responseContent: unknown;

		await BaseApi.dispatchCookieOnChangeEvent();

		const contentType = response.headers.get("content-type");

		if (contentType?.includes("application/json")) {
			responseContent = await response.json();
		} else if (contentType?.includes("application/zip")) {
			responseContent = await response.blob();
		} else {
			responseContent = await response.text();
		}

		if (!response.ok) {
			return Promise.reject(responseContent);
		}

		const subscriptionId = response.headers.get("X-Investor-Subscription");
		const revision = response.headers.get("X-Investor-Revision");
		if (subscriptionId && revision !== null) {
			if (mutation || !BaseApi.investorRevisions.has(subscriptionId)) BaseApi.investorRevisions.set(subscriptionId, revision);
			const entityId = response.url.match(/\/(?:subscriptions(?:\/draft-subscription)?|product_subscribed)\/([0-9a-f-]{36})(?:\/|$)/i)?.[1];
			if (entityId) BaseApi.investorSubscriptions.set(entityId, subscriptionId);
			if (responseContent && typeof responseContent === "object" && "productsSubscribed" in responseContent) {
				for (const product of (responseContent as { productsSubscribed?: { id: string }[] }).productsSubscribed ?? [])
					BaseApi.investorSubscriptions.set(product.id, subscriptionId);
			}
		}
		return responseContent as T;
	}

	protected static dispatchCookieOnChangeEvent = (() => {
		let previousJwt: string | null = null;

		const getJwtPair = () => {
			let jwtPairString: string | undefined = undefined;

			document.cookie.split(";").forEach((cookie) => {
				const [key, value] = cookie.split("=").map((c) => c.trim());

				if (key !== jwtCookieName) return;
				jwtPairString = value;
			});
			if (!jwtPairString) return null;
			return decodeURIComponent(jwtPairString);
		};

		const mayDispatchEvent = async () => {
			try {
				const jwtPair = getJwtPair();
				if (previousJwt !== jwtPair) {
					previousJwt = jwtPair;
					document.dispatchEvent(
						new CustomEvent("jwt_cookie_change", {
							detail: jwtPair,
						}),
					);
				}

				return Promise.resolve(jwtPair);
			} catch (error) {
				return Promise.reject(error);
			}
		};

		mayDispatchEvent();
		return mayDispatchEvent;
	})();
}
